import { z } from 'zod';

import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import {
  buildStockReceiptWritePayload,
  createEmptyLine,
  createEmptyPackage,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';
import {
  stockReceiptFormObjectSchema,
} from '#/modules/imsMgmt/receipts/stockReceipt/schemas/stockReceiptSchema';
import { ROUNDING_MODE, resolveRoundingMode } from '#/utils/roundingMode';

/** PO detail → UI 倉別（僅供儲位 options；nested payload 仍省略 warehouseId）。 */
function resolvePurchaseOrderReceiptWarehouseId(detail) {
  return detail?.receiptWarehouse?.id
    ?? detail?.receiptWarehouseId
    ?? detail?.defaultWarehouse?.id
    ?? detail?.defaultWarehouseId
    ?? '';
}

/**
 * PO／排程建入庫：倉別必填（供 FE 載儲位 options）；payload 仍省略 warehouseId。
 * IMS 手動建入庫仍用 stockReceiptFormSchema。
 * 必須 extend **object** schema（含 superRefine 的 schema 不可 overwrite keys）。
 */
export const purchaseOrderStockReceiptFormSchema = stockReceiptFormObjectSchema.extend({
  warehouseId: z.string().min(1, '請選擇倉別'),
});

/**
 * PO line 選定單位（入庫 line.unitId）。
 * 優先 `line.unit`／既有 `unitId`；空時才退回 base。
 */
export function resolvePurchaseOrderLineSelectedUnitId(line) {
  return resolveUnitId(line?.unit)
    || String(line?.unitId ?? '').trim()
    || line?.baseUnit?.id
    || line?.mtr?.baseUnitId
    || line?.mtr?.baseUnit?.id
    || '';
}

/**
 * @deprecated 改用 `resolvePurchaseOrderLineSelectedUnitId`（單位在 line，非 package）
 */
export function resolvePurchaseOrderLineBaseUnitId(line) {
  return resolvePurchaseOrderLineSelectedUnitId(line);
}

/** PO detail line → 入庫表單列；`unitId` 在 line；packages 不帶單位。 */
export function mapPurchaseOrderLineToReceiptFormLine(line) {
  const mtrId = line?.mtr?.id ?? '';
  if (!mtrId) {
    return null;
  }

  return createEmptyLine({
    mtrId,
    mtrNo: line?.mtr?.mtrNo ?? '',
    mtrName: line?.mtr?.name ?? '',
    unitId: resolvePurchaseOrderLineSelectedUnitId(line),
    unitOptions: line?.mtr?.unitOptions ?? line?.unitOptions ?? [],
    unitPrice: line?.unitPrice ?? '',
    remark: line?.remark ?? '',
    packages: [createEmptyPackage()],
  });
}

/** PO detail → 建入庫表單 defaultValues（預填倉別／幣別供 UI；payload 仍省略 warehouseId）。 */
export function buildPurchaseOrderStockReceiptDefaultValues(detail) {
  const lines = (detail?.lines ?? [])
    .map(mapPurchaseOrderLineToReceiptFormLine)
    .filter(Boolean);

  return {
    receiptDate: '',
    expectedArrivalDate: '',
    warehouseId: resolvePurchaseOrderReceiptWarehouseId(detail),
    supplierId: detail?.supplier?.id ?? detail?.supplierId ?? '',
    currencyId: detail?.currencyId ?? detail?.currency?.id ?? '',
    roundingMode: resolveRoundingMode(
      detail?.roundingMode ?? detail?.amountSummary?.roundingMode,
      ROUNDING_MODE.HALF_UP,
    ),
    remark: '',
    issueReason: detail?.issueReason ?? '',
    customsDutyAmount: '',
    freightAmount: '',
    sourceType: 'PURCHASE',
    sourceId: detail?.id ?? '',
    lines,
  };
}

/**
 * 表單 → POST …/{poId}/stock-receipt（或排程 nested）body（camel）。
 * **只送** receiptDate、remark、issueReason、關稅／運費、lines（packages：locationId／qty／remark）。
 * **省略** supplierId／currencyId／roundingMode／warehouseId／expectedArrivalDate／
 * sourceType／sourceId（後端繼承 PO／排程商業與倉別 snapshot）。
 */
export function buildPurchaseOrderStockReceiptPayload(values) {
  const payload = buildStockReceiptWritePayload({
    ...values,
    sourceType: 'PURCHASE',
  });
  delete payload.warehouseId;
  return payload;
}
